import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { Link } from "react-router-dom";
import { addNotification } from "../../store/slices/uiSlice";
import { fetchUsers } from "../../store/slices/userSlice";
import { API_URL } from "../../config/api";

const isAnnouncementPublished = (announcement) =>
  announcement?.isPublished === true || announcement?.published === true;

const isAnnouncementPublic = (announcement) => {
  if (announcement?.isPublic !== undefined) return announcement.isPublic !== false;
  if (announcement?.public !== undefined) return announcement.public !== false;
  return true;
};

const apiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) {
    return data.replace(/^Error:\s*/i, "").trim();
  }
  return data?.error || data?.message || fallback;
};

const AnnouncementPanel = () => {
  const { user, token } = useSelector((state) => state.auth);
  const { users } = useSelector((state) => state.users);
  const dispatch = useDispatch();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    type: "INFO",
    isPublic: true,
    targetUserIds: [],
    category: "GENERAL",
    validityType: "PERMANENT",
    validityValue: 2,
    validityUnit: "DAYS",
  });
  const [processing, setProcessing] = useState({});
  const [expandedReportId, setExpandedReportId] = useState(null);
  const [ackReports, setAckReports] = useState({});
  const [loadingReportId, setLoadingReportId] = useState(null);

  useEffect(() => {
    fetchAnnouncements();
    dispatch(fetchUsers());
  }, [dispatch]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/announcements`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setAnnouncements(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to fetch announcements:", error);
      dispatch(
        addNotification({
          type: "error",
          message: apiErrorMessage(error, "Failed to fetch announcements"),
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingAnnouncement(null);
    setFormData({
      title: "",
      content: "",
      type: "INFO",
      isPublic: true,
      targetUserIds: [],
      category: "GENERAL",
      validityType: "PERMANENT",
      validityValue: 2,
      validityUnit: "DAYS",
    });
    setShowCreateModal(true);
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    const isPublic = isAnnouncementPublic(announcement);
    const targetUserIds = Array.isArray(announcement.targetUserIds)
      ? announcement.targetUserIds
      : [];
    setFormData({
      title: announcement.title,
      content: announcement.content,
      type: announcement.type || "INFO",
      isPublic,
      targetUserIds,
      category: announcement.category || "GENERAL",
      validityType: announcement.validityType || "PERMANENT",
      validityValue: announcement.validityValue || 2,
      validityUnit: announcement.validityUnit || "DAYS",
    });
    setShowCreateModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setProcessing({ ...processing, submit: true });

      if (editingAnnouncement) {
        // Update existing announcement
        await axios.put(
          `${API_URL}/announcements/${editingAnnouncement.id}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        dispatch(
          addNotification({
            type: "success",
            message: "Announcement updated successfully",
          })
        );
      } else {
        // Create new announcement
        await axios.post(`${API_URL}/announcements`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        dispatch(
          addNotification({
            type: "success",
            message: "Announcement created successfully",
          })
        );
      }

      setShowCreateModal(false);
      fetchAnnouncements();
    } catch (error) {
      console.error("Failed to save announcement:", error);
      dispatch(
        addNotification({
          type: "error",
          message: apiErrorMessage(error, "Failed to save announcement"),
        })
      );
    } finally {
      setProcessing({ ...processing, submit: false });
    }
  };

  const handlePublish = async (announcementId) => {
    try {
      setProcessing({ ...processing, [announcementId]: "publishing" });
      await axios.post(
        `${API_URL}/announcements/${announcementId}/publish`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      dispatch(
        addNotification({
          type: "success",
          message: "Announcement published successfully",
        })
      );

      fetchAnnouncements();
    } catch (error) {
      console.error("Failed to publish announcement:", error);
      dispatch(
        addNotification({
          type: "error",
          message: apiErrorMessage(error, "Failed to publish announcement"),
        })
      );
    } finally {
      setProcessing({ ...processing, [announcementId]: null });
    }
  };

  const handleUnpublish = async (announcementId) => {
    try {
      setProcessing({ ...processing, [announcementId]: "unpublishing" });
      await axios.post(
        `${API_URL}/announcements/${announcementId}/unpublish`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      dispatch(
        addNotification({
          type: "success",
          message: "Announcement unpublished successfully",
        })
      );

      fetchAnnouncements();
    } catch (error) {
      console.error("Failed to unpublish announcement:", error);
      dispatch(
        addNotification({
          type: "error",
          message: apiErrorMessage(error, "Failed to unpublish announcement"),
        })
      );
    } finally {
      setProcessing({ ...processing, [announcementId]: null });
    }
  };

  const handleDelete = async (announcementId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this announcement? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      setProcessing({ ...processing, [announcementId]: "deleting" });
      await axios.delete(`${API_URL}/announcements/${announcementId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      dispatch(
        addNotification({
          type: "success",
          message: "Announcement deleted successfully",
        })
      );

      fetchAnnouncements();
    } catch (error) {
      console.error("Failed to delete announcement:", error);
      dispatch(
        addNotification({
          type: "error",
          message: apiErrorMessage(error, "Failed to delete announcement"),
        })
      );
    } finally {
      setProcessing({ ...processing, [announcementId]: null });
    }
  };

  const formatValidity = (announcement) => {
    const type = announcement.validityType || "PERMANENT";
    if (type === "PERMANENT") return "Permanent";
    const value = announcement.validityValue;
    const unit = (announcement.validityUnit || "DAYS").toLowerCase();
    if (!value) return "Timed";
    const unitLabel = value === 1 ? unit.replace(/s$/, "") : unit;
    return `${value} ${unitLabel}`;
  };

  const fetchAcknowledgementReport = async (announcementId) => {
    if (expandedReportId === announcementId) {
      setExpandedReportId(null);
      return;
    }

    setExpandedReportId(announcementId);

    if (ackReports[announcementId]) return;

    try {
      setLoadingReportId(announcementId);
      const response = await axios.get(
        `${API_URL}/announcements/${announcementId}/acknowledgements`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAckReports((prev) => ({ ...prev, [announcementId]: response.data }));
    } catch (error) {
      console.error("Failed to fetch acknowledgement report:", error);
      dispatch(
        addNotification({
          type: "error",
          message: apiErrorMessage(error, "Failed to fetch acknowledgement report"),
        })
      );
      setExpandedReportId(null);
    } finally {
      setLoadingReportId(null);
    }
  };

  const refreshAcknowledgementReport = async (announcementId) => {
    try {
      setLoadingReportId(announcementId);
      const response = await axios.get(
        `${API_URL}/announcements/${announcementId}/acknowledgements`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAckReports((prev) => ({ ...prev, [announcementId]: response.data }));
    } catch (error) {
      console.error("Failed to refresh acknowledgement report:", error);
    } finally {
      setLoadingReportId(null);
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "INFO":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
      case "WARNING":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
      case "SUCCESS":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "ERROR":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        <Link to="/chat">
          <span className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 32 32"
              className="w-4 h-4 inline-block mr-2 align-middle"
            >
              <path
                d="M32 15H3.41l8.29-8.29-1.41-1.42-10 10a1 1 0 0 0 0 1.41l10 10 1.41-1.41L3.41 17H32z"
                data-name="4-Arrow Left"
              />
            </svg>
            Back to Dashboard
          </span>
        </Link>
      </p>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Announcement Panel
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Create and manage organization announcements
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors flex items-center space-x-2"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          <span>Create Announcement</span>
        </button>
      </div>

      {announcements.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
            No announcements
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Get started by creating a new announcement
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {announcements.map((announcement) => {
            const published = isAnnouncementPublished(announcement);
            const isPublic = isAnnouncementPublic(announcement);
            return (
            <div
              key={announcement.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {announcement.title}
                    </h3>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(
                        announcement.type
                      )}`}
                    >
                      {announcement.type}
                    </span>
                    <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded-full">
                      {announcement.category || "GENERAL"}
                    </span>
                    {isPublic ? (
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
                        Public
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 rounded-full">
                        Private
                      </span>
                    )}
                    {published ? (
                      <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full">
                        Published
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 rounded-full">
                        Draft
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                    Created by {announcement.createdByName} on{" "}
                    {formatDate(announcement.createdAt)}
                  </p>
                  {published && announcement.publishedAt && (
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      Published on {formatDate(announcement.publishedAt)}
                      {" · "}
                      Validity: {formatValidity(announcement)}
                      {announcement.expiresAt && (
                        <> · Expires {formatDate(announcement.expiresAt)}</>
                      )}
                    </p>
                  )}
                  {!published && (
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      Validity: {formatValidity(announcement)}
                    </p>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEdit(announcement)}
                    disabled={processing[announcement.id]}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </button>
                  {published ? (
                    <button
                      onClick={() => handleUnpublish(announcement.id)}
                      disabled={processing[announcement.id]}
                      className="p-2 text-gray-600 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title="Unpublish"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                        />
                      </svg>
                    </button>
                  ) : (
                    <button
                      onClick={() => handlePublish(announcement.id)}
                      disabled={processing[announcement.id]}
                      className="p-2 text-gray-600 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title="Publish"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(announcement.id)}
                    disabled={processing[announcement.id]}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="prose dark:prose-invert max-w-none mb-4">
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {announcement.content}
                </p>
              </div>

              {published && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <button
                    onClick={() => fetchAcknowledgementReport(announcement.id)}
                    className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                  >
                    {expandedReportId === announcement.id
                      ? "Hide Acknowledgements"
                      : "View Acknowledgements"}
                  </button>

                  {expandedReportId === announcement.id && (
                    <div className="mt-4">
                      {loadingReportId === announcement.id &&
                      !ackReports[announcement.id] ? (
                        <div className="flex items-center justify-center py-6">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                        </div>
                      ) : ackReports[announcement.id] ? (
                        <div>
                          <div className="flex items-center justify-between mb-4">
                            <span className="px-3 py-1 text-sm font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200 rounded-full">
                              {ackReports[announcement.id].acknowledgedCount} /{" "}
                              {ackReports[announcement.id].totalEligible}{" "}
                              acknowledged
                            </span>
                            <button
                              onClick={() =>
                                refreshAcknowledgementReport(announcement.id)
                              }
                              disabled={loadingReportId === announcement.id}
                              className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-50"
                            >
                              Refresh
                            </button>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                                Acknowledged (
                                {ackReports[announcement.id].acknowledgedCount})
                              </h4>
                              {ackReports[announcement.id].acknowledgedUsers
                                .length === 0 ? (
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  No acknowledgements yet
                                </p>
                              ) : (
                                <ul className="max-h-48 overflow-y-auto space-y-1">
                                  {ackReports[announcement.id].acknowledgedUsers.map(
                                    (u) => (
                                      <li
                                        key={u.userId}
                                        className="text-sm text-gray-700 dark:text-gray-300 flex justify-between gap-2"
                                      >
                                        <span>{u.name}</span>
                                        <span className="text-xs text-gray-400 shrink-0">
                                          {formatDate(u.acknowledgedAt)}
                                        </span>
                                      </li>
                                    )
                                  )}
                                </ul>
                              )}
                            </div>

                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                                Pending (
                                {ackReports[announcement.id].pendingCount})
                              </h4>
                              {ackReports[announcement.id].pendingUsers
                                .length === 0 ? (
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  Everyone has acknowledged
                                </p>
                              ) : (
                                <ul className="max-h-48 overflow-y-auto space-y-1">
                                  {ackReports[announcement.id].pendingUsers.map(
                                    (u) => (
                                      <li
                                        key={u.userId}
                                        className="text-sm text-gray-700 dark:text-gray-300"
                                      >
                                        {u.name}
                                      </li>
                                    )
                                  )}
                                </ul>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          onClick={() => setShowCreateModal(false)}
        >
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-primary-600 px-6 py-4 flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">
                  {editingAnnouncement
                    ? "Edit Announcement"
                    : "Create Announcement"}
                </h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-white hover:text-gray-200 focus:outline-none"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="px-6 py-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Enter announcement title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Category *
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) =>
                        setFormData({ ...formData, category: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="GENERAL">General</option>
                      <option value="TASK">Task</option>
                      <option value="ATTENDANCE">Attendance</option>
                    </select>
                    {formData.category === "GENERAL" && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        General announcements will also be posted to the General
                        channel
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Type
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) =>
                        setFormData({ ...formData, type: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="INFO">Info</option>
                      <option value="WARNING">Warning</option>
                      <option value="SUCCESS">Success</option>
                      <option value="ERROR">Error</option>
                    </select>
                  </div>

                  <div>
                    <label className="flex items-center space-x-2 mb-2">
                      <input
                        type="checkbox"
                        checked={formData.isPublic}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            isPublic: e.target.checked,
                            targetUserIds: e.target.checked
                              ? []
                              : formData.targetUserIds,
                          })
                        }
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Public Announcement (visible to everyone)
                      </span>
                    </label>
                    {!formData.isPublic && (
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Select Users (Private Announcement)
                        </label>
                        <div className="max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-700">
                          {users
                            .filter((u) => u.id !== user?.id)
                            .map((userItem) => (
                              <label
                                key={userItem.id}
                                className="flex items-center space-x-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={formData.targetUserIds.includes(
                                    userItem.id
                                  )}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFormData({
                                        ...formData,
                                        targetUserIds: [
                                          ...formData.targetUserIds,
                                          userItem.id,
                                        ],
                                      });
                                    } else {
                                      setFormData({
                                        ...formData,
                                        targetUserIds:
                                          formData.targetUserIds.filter(
                                            (id) => id !== userItem.id
                                          ),
                                      });
                                    }
                                  }}
                                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                  {userItem.firstName} {userItem.lastName} (@
                                  {userItem.username})
                                </span>
                              </label>
                            ))}
                        </div>
                        {formData.targetUserIds.length === 0 && (
                          <p className="mt-1 text-xs text-red-500">
                            Please select at least one user for private
                            announcements
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Validity
                    </label>
                    <select
                      value={formData.validityType}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          validityType: e.target.value,
                        })
                      }
                      disabled={isAnnouncementPublished(editingAnnouncement)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="PERMANENT">Permanent</option>
                      <option value="TIMED">Timed</option>
                    </select>
                    {formData.validityType === "TIMED" && (
                      <div className="mt-3 flex items-center gap-3">
                        <input
                          type="number"
                          min={1}
                          required
                          value={formData.validityValue}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              validityValue: parseInt(e.target.value, 10) || 1,
                            })
                          }
                          disabled={isAnnouncementPublished(editingAnnouncement)}
                          className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <select
                          value={formData.validityUnit}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              validityUnit: e.target.value,
                            })
                          }
                          disabled={isAnnouncementPublished(editingAnnouncement)}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <option value="DAYS">Days</option>
                          <option value="WEEKS">Weeks</option>
                          <option value="MONTHS">Months</option>
                        </select>
                      </div>
                    )}
                    {isAnnouncementPublished(editingAnnouncement) && (
                      <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                        Unpublish to change validity settings.
                      </p>
                    )}
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Timed announcements stop appearing in the popup after expiry.
                      Users who join after expiry will not see them.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Content *
                    </label>
                    <textarea
                      required
                      rows={8}
                      value={formData.content}
                      onChange={(e) =>
                        setFormData({ ...formData, content: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                      placeholder="Enter announcement content"
                    />
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={
                      processing.submit ||
                      (!formData.isPublic &&
                        formData.targetUserIds.length === 0) ||
                      (formData.validityType === "TIMED" &&
                        (!formData.validityValue || formData.validityValue < 1))
                    }
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processing.submit
                      ? "Saving..."
                      : editingAnnouncement
                      ? "Update"
                      : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementPanel;
